import { useEffect, useState } from 'react';

import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { EnvVars } from '../../../environment';
import { useUserContext } from '../../Contexts/User';
import { useHasPermission } from '../../hooks/useHasPermission';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatter } from '@/api/queries/useGetMatter';
import { Icon } from '@/new/components/icon';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/new/components/shadcn/sidebar';
import { cn } from '@/new/lib/utils';

interface SidebarLink {
  text: string;
  iconClass: string;
  href?: string;
  onClick?: () => void;
  requiredPermissions?: Permission[];
  requiresGlobalAdmin?: boolean;
  pathRegex?: RegExp;
}

export function AppSidebar() {
  const params = useParams();
  const location = useLocation();
  const authInfo = useAuthInfo();
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();

  const [sidebarItems, setSidebarItems] = useState<SidebarLink[][]>();

  const { data: matter } = useGetMatter(params.matterId);
  const { data: subset } = useGetCase(params.subsetId ?? null);

  async function getBullMqLink() {
    const accessToken = await authInfo.tokens.getAccessToken();
    window.open(`${EnvVars.API_URL}/admin/queues/?token=${encodeURIComponent(accessToken ?? '')}`, '_blank');
  }

  const rootItems: SidebarLink[][] = [
    [{ iconClass: 'lucide--folders', text: 'Matters', href: '/matters' }],
    [
      { iconClass: 'lucide--file-question-mark', text: 'Guide', href: '/guide' },
      { iconClass: 'lucide--folder', text: 'Client Materials', href: '/client-materials' },
      { iconClass: 'lucide--notebook', text: 'Release Notes', href: '/release-notes' },
    ],
    [
      {
        iconClass: 'lucide--chart-column',
        text: 'Usage',
        href: '/usage',
        requiredPermissions: [Permission.UsageDashboardView],
      },
      {
        iconClass: 'lucide--users-round',
        text: 'Manage Users',
        href: '/manage-users',
        requiredPermissions: [Permission.UsersManage],
      },
      {
        iconClass: 'lucide--building',
        text: 'Org Settings',
        href: '/organisation-settings/credits',
        requiredPermissions: [Permission.IntegrationsManage, Permission.TokenLimitsManage],
      },
    ],
    [
      {
        iconClass: 'lucide--shield-user',
        text: 'Admin',
        href: '/admin/organisations',
        requiresGlobalAdmin: true,
      },
      {
        iconClass: 'lucide--target',
        text: 'BullMQ',
        onClick: getBullMqLink,
        requiresGlobalAdmin: true,
      },
    ],
  ];

  const allDocumentsSubsetId = matter?.allDocumentsCaseId ?? null;

  const matterItems: SidebarLink[][] = [
    [
      {
        iconClass: 'lucide--info',
        text: 'Matter Dashboard',
        href: `/matters/${params.matterId}`,
      },
      {
        iconClass: 'lucide--group',
        text: 'Subsets',
        href: `/matters/${params.matterId}/subsets`,
        pathRegex: /\/matters\/[^/]+\/subsets(?!\/)/,
      },
      {
        iconClass: 'lucide--settings',
        text: 'Settings',
        href: `/matters/${params.matterId}/settings/metadata`,
        pathRegex: /\/matters\/[^/]+\/settings/,
      },
    ],
    // Subset-level items linked to All Documents subset
    ...(allDocumentsSubsetId
      ? [
          [
            {
              iconClass: 'lucide--bot',
              text: 'Kim',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/kim`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/kim/,
            },
            {
              iconClass: 'lucide--search',
              text: 'Facts',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/facts`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/facts/,
            },
            {
              iconClass: 'lucide--git-fork',
              text: 'Chronologies',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/chronologies`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies/,
            },
          ],
          [
            {
              iconClass: 'lucide--shield-alert',
              text: 'Inconsistencies',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/inconsistencies`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/inconsistencies/,
            },
            {
              iconClass: 'lucide--folder',
              text: 'Reports',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/reports`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/reports/,
            },
          ],
          [
            {
              iconClass: 'lucide--file-text',
              text: 'Documents',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/docs`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/docs/,
            },
            {
              iconClass: 'lucide--users-round',
              text: 'Entities',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/entities`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/entities/,
            },
          ],
          [
            {
              iconClass: 'lucide--radio',
              text: 'Realtime',
              href: `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}/realtime`,
              pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/realtime/,
              requiredPermissions: [Permission.RealtimeUse],
            },
          ],
        ]
      : []),
  ];

  const subsetItems: SidebarLink[][] = [
    [
      {
        iconClass: 'lucide--info',
        text: 'Overview',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}`,
      },
    ],
    [
      {
        iconClass: 'lucide--bot',
        text: 'Kim',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/kim`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/kim/,
      },
      {
        iconClass: 'lucide--search',
        text: 'Facts',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/facts`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/facts/,
      },
      {
        iconClass: 'lucide--git-fork',
        text: 'Chronologies',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/chronologies`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies/,
      },
    ],
    [
      {
        iconClass: 'lucide--shield-alert',
        text: 'Inconsistencies',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/inconsistencies`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/inconsistencies/,
      },
      {
        iconClass: 'lucide--folder',
        text: 'Reports',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/reports`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/reports/,
      },
    ],
    [
      {
        iconClass: 'lucide--file-text',
        text: 'Documents',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/docs`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/docs/,
      },
      {
        iconClass: 'lucide--users-round',
        text: 'Entities',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/entities`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/entities/,
      },
    ],
    [
      {
        iconClass: 'lucide--radio',
        text: 'Realtime',
        href: `/matters/${params.matterId}/subsets/${params.subsetId}/realtime`,
        pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/realtime/,
        requiredPermissions: [Permission.RealtimeUse],
      },
    ],
  ];

  const adminItems: SidebarLink[][] = [
    rootItems[0],
    [
      {
        iconClass: 'lucide--shield-user',
        text: 'Admin',
        href: '/admin/organisations',
        requiresGlobalAdmin: true,
      },
    ],
    [
      {
        iconClass: 'lucide--settings',
        text: 'Org Settings',
        href: `/admin/organisations/${params.organisationId}`,
        requiresGlobalAdmin: true,
      },
      {
        iconClass: 'lucide--users-round',
        text: 'Manage Users',
        href: `/admin/organisations/${params.organisationId}/users`,
        requiresGlobalAdmin: true,
      },
      {
        iconClass: 'lucide--zap',
        text: 'Manage Runs',
        href: `/admin/organisations/${params.organisationId}/runs`,
        requiresGlobalAdmin: true,
      },
    ],
  ];

  // Set sidebar items depending on route
  useEffect(() => {
    // If viewing All Documents subset, show Matter context items (not subset items)
    if (params.subsetId && allDocumentsSubsetId !== params.subsetId) return setSidebarItems(subsetItems);
    // Don't switch to matter context if we're in the creator flow
    if (params.matterId && !location.pathname.includes('/matters/new/')) return setSidebarItems(matterItems);
    if (params.organisationId && location.pathname.startsWith('/admin')) return setSidebarItems(adminItems);
    setSidebarItems(rootItems);
  }, [location, allDocumentsSubsetId]);

  const { state } = useSidebar();
  const isInMatterContext = params.matterId !== undefined && !location.pathname.includes('/matters/new/');
  const isInSubsetContext = params.subsetId !== undefined && allDocumentsSubsetId !== params.subsetId;
  const logoutFn = useLogoutFunction();

  return (
    <Sidebar variant="inset" collapsible="icon" className="static w-60" data-tour="sidebar">
      <SidebarContent className="p-2.5 py-1.5 pr-0">
        <SidebarHeader onClick={(e) => e.stopPropagation()} className="-ml-0.5">
          <Link to="/" className="relative w-24">
            <img
              src="/logo-full.svg"
              alt="wexler.ai"
              className={`${state === 'collapsed' ? 'opacity-0' : 'opacity-100'} absolute duration-150`}
            />
            <img src="/logo-w.svg" alt="wexler.ai" />
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              className={`${state === 'collapsed' ? 'h-0 opacity-0' : 'h-10 opacity-100'} flex w-full items-center justify-between gap-1 pr-1 duration-150`}
            >
              <div className="space-y-0.5 overflow-hidden text-left text-xs">
                <p className="line-clamp-1 text-ellipsis">{user?.email}</p>
                <p className="line-clamp-1 text-ellipsis text-gray-500">{user?.organisation?.name}</p>
              </div>
              <Icon className="lucide--chevron-down text-gray-500" />
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => logoutFn(true)}>Log Out</DropdownMenuItem>
                <DropdownMenuItem>
                  <Link to="/settings">User Settings</Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarHeader>

        {/* Matter/Matters header - hidden when collapsed */}
        {state !== 'collapsed' && (
          <Link
            onClick={(e) => e.stopPropagation()}
            to={isInMatterContext ? `/matters/${params.matterId}` : '/matters'}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 pt-1.5 text-sm transition-colors',
              isInMatterContext && 'font-medium text-gray-600',
              !isInMatterContext && 'text-gray-600 hover:text-gray-900',
            )}
          >
            <Icon
              className={cn(
                isInMatterContext || location.pathname === '/matters'
                  ? isInMatterContext
                    ? 'lucide--folder text-teal-500'
                    : 'lucide--folders text-teal-500'
                  : 'lucide--folders text-gray-500',
                'size-4',
              )}
            />
            <div className="line-clamp-1 flex items-center gap-3.25 overflow-ellipsis">
              <AnimatePresence mode="wait">
                <span
                  key={isInMatterContext ? 'matter-name' : 'matters'}
                  className={cn(
                    'line-clamp-1 pl-1.25 overflow-ellipsis whitespace-nowrap',
                    location.pathname === '/matters' && 'text-black',
                  )}
                >
                  {isInMatterContext && matter ? `${matter.name}` : 'Matters'}
                </span>
              </AnimatePresence>
              {location.pathname === '/matters' && (
                <span className="rounded-full bg-teal-400 p-0.5 duration-150"></span>
              )}
            </div>
          </Link>
        )}

        {/* Subset indicator - shows when in subset context, hidden when collapsed */}
        {state !== 'collapsed' && (
          <AnimatePresence>
            {isInSubsetContext && subset && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
                className="pl-1.25"
              >
                <Link
                  onClick={(e) => e.stopPropagation()}
                  to={`/matters/${params.matterId}/subsets/${params.subsetId}`}
                  className="flex items-center gap-2 rounded-md px-2 text-sm font-medium text-gray-600"
                >
                  <Icon className="lucide--corner-down-right size-3.75 text-sky-500" />
                  <Icon className="lucide--group size-4 text-sky-500" />
                  <span className="line-clamp-1 pl-1 overflow-ellipsis whitespace-nowrap">{subset.name}</span>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Remaining items - animate in/out based on context, no indent when collapsed */}
        <AnimatePresence mode="wait">
          <motion.div
            key={isInSubsetContext ? 'subset-items' : isInMatterContext ? 'matter-items' : 'root-items'}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{
              duration: 0.15,
              delay: isInSubsetContext ? 0.15 : 0,
              ease: [0.4, 0, 0.2, 1],
            }}
            className={cn(
              state !== 'collapsed' &&
                isInMatterContext &&
                !isInSubsetContext &&
                'ml-[15px] border-l border-teal-200 pl-[8px]',
              state !== 'collapsed' && isInSubsetContext && 'ml-[16px] border-l border-sky-200 pl-[11px]',
            )}
          >
            {sidebarItems &&
              sidebarItems.slice(state === 'collapsed' ? 0 : isInMatterContext ? 0 : 1).map((group, i) => {
                const filteredItems = group.filter((item) => {
                  if (item.requiresGlobalAdmin && !user?.organisation.isGlobalAdmin) return false;
                  if (item.requiredPermissions) {
                    for (const permission of item.requiredPermissions) {
                      if (!hasPermission(permission)) return false;
                    }
                  }
                  return true;
                });
                if (filteredItems.length === 0) return null;

                return (
                  <SidebarGroup key={i}>
                    <SidebarGroupContent>
                      <SidebarMenu className="text-gray-600">
                        {filteredItems.map((item) => {
                          const selected = item.pathRegex
                            ? item.pathRegex.test(location.pathname)
                            : item.href === location.pathname;

                          return (
                            <SidebarMenuItem className="w-full" key={item.href ?? item.text}>
                              {item.href ? (
                                <SidebarMenuButton asChild className="group" tooltip={item.text}>
                                  <Link to={item.href}>
                                    <span
                                      className={cn(
                                        'iconify size-4 shrink-0 duration-150',
                                        item.iconClass,
                                        selected
                                          ? isInSubsetContext
                                            ? 'text-sky-500'
                                            : 'text-teal-500'
                                          : 'text-gray-500',
                                      )}
                                    ></span>
                                    <div
                                      className={cn(
                                        '[data-state=collapsed]:animate-out [data-state=expanded]:animate-in fade-in fade-out duration-150 hover:text-gray-900',
                                        'flex items-center gap-3.25',
                                        selected && 'text-black',
                                      )}
                                    >
                                      <span className="line-clamp-1 overflow-ellipsis whitespace-nowrap">
                                        {item.text}
                                      </span>
                                      <span
                                        className={cn(
                                          'rounded-full p-0.5 duration-150',
                                          selected ? 'opacity-100' : 'opacity-0',
                                          isInSubsetContext ? 'bg-sky-400' : 'bg-teal-400',
                                        )}
                                      ></span>
                                    </div>
                                  </Link>
                                </SidebarMenuButton>
                              ) : (
                                <SidebarMenuButton onClick={item.onClick} tooltip={item.text}>
                                  <span className={cn('iconify size-4 shrink-0 text-gray-500', item.iconClass)}></span>
                                  <span className="[data-state=collapsed]:animate-out [data-state=expanded]:animate-in fade-in fade-out">
                                    {item.text}
                                  </span>
                                </SidebarMenuButton>
                              )}
                            </SidebarMenuItem>
                          );
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                );
              })}
          </motion.div>
        </AnimatePresence>
      </SidebarContent>
    </Sidebar>
  );
}
